import * as Flux from 'dg-web-shared/lib/Flux';
import * as Highlight from 'dg-web-shared/lib/Highlight';
import { getOrElse, isDefined, thenElse } from 'dg-web-shared/lib/MaybeV2';
import * as Sort from 'dg-web-shared/lib/Sort';
import * as String from 'dg-web-shared/lib/String';
import * as Icons48 from 'dg-web-shared/ui/icons/Icons48';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import * as Text from '../../../common/i18n/Text';
import * as SettingsState from '../../../common/state/SettingsState';
import { SearchField, SearchFieldStyle } from '../../../ui/SearchField';
import {
    SlideInContentBody,
    SlideInContentFixedColumn,
    SlideInContentFlexColumn,
    SlideInContentHeader,
    SlideInContentHeaderSortableItem,
    SlideInContentItem,
    SlideInContentItemText,
    SlideInSearchFieldContainer,
} from '../../../ui/slidein/SlideIn';
import { logAction } from '../../../utils/ActionLog';
import * as DropinActions from '../../actions/DropinActions';
import * as ParkCreateActions from '../../actions/ParkCreateActions';
import * as DropinTexts from '../../i18n/DropinTexts';
import { ParkCreatePortalSlideIn } from '../../ParkCreateSlideIn';
import * as ParkOptionListState from '../../state/ParkOptionListState';
import * as ZoneDropinState from '../../state/ZoneDropinState';
import { CitiesState, ZonesOfCityState } from '../ParkOptionList';
import { ArrowPosition, InlineInfoBox } from '../../../ui/modals/Confirmable';
import { css } from '@emotion/css';
import { Colors } from 'dg-web-shared/ui/vars';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import { useRef, type JSX } from 'react';
import { IconBig } from 'dg-web-shared/tb-ui/icons/IconBig.tsx';

export interface Texts {
    Title: (city: string) => string;
    TitleSearch: Text.Translation;
    ZoneIdLabel: Text.Translation;
    ZoneNameLabel: Text.Translation;
}

function sortZones(
    zones: ZonesOfCityState.Zone[],
    sortField: ZoneDropinState.SortField,
    sortAscending: boolean,
    filterValue: string,
): ZonesOfCityState.Zone[] {
    switch (sortField) {
        case ZoneDropinState.SortField.ZoneName:
            return sortByName(zones, sortAscending, filterValue);
        case ZoneDropinState.SortField.Distance:
            return sortByDistance(zones, sortAscending, filterValue);
        default:
            return sortByExtZoneCode(zones, sortAscending, filterValue);
    }
}

function sortPerfectMatch(
    a: ZonesOfCityState.Zone,
    b: ZonesOfCityState.Zone,
    filterValue: string,
) {
    const aMatch = a.extZoneCode?.toString() === filterValue;
    const bMatch = b.extZoneCode?.toString() === filterValue;
    if (aMatch && !bMatch) return -1;
    if (!aMatch && bMatch) return 1;
    return 0;
}

function sortByExtZoneCode(
    zones: ZonesOfCityState.Zone[],
    sortAscending: boolean,
    filterValue: string,
): ZonesOfCityState.Zone[] {
    return zones.sort((a: ZonesOfCityState.Zone, b: ZonesOfCityState.Zone) => {
        const p = sortPerfectMatch(a, b, filterValue);
        if (p !== 0) return p;

        return Sort.arithmeticCompare(
            a.extZoneCode || 100000,
            b.extZoneCode || 100000,
            sortAscending,
        );
    });
}

function sortByDistance(
    zones: ZonesOfCityState.Zone[],
    sortAscending: boolean,
    filterValue: string,
): ZonesOfCityState.Zone[] {
    return zones.sort((a: ZonesOfCityState.Zone, b: ZonesOfCityState.Zone) => {
        const p = sortPerfectMatch(a, b, filterValue);
        if (p !== 0) return p;

        return Sort.numberCompare(
            Math.min(a.distance || 0, 10000),
            Math.min(b.distance || 0, 10000),
            sortAscending,
            false,
        );
    });
}

function sortByName(
    zones: ZonesOfCityState.Zone[],
    sortAscending: boolean,
    filterValue: string,
): ZonesOfCityState.Zone[] {
    return zones.sort((a: ZonesOfCityState.Zone, b: ZonesOfCityState.Zone) => {
        const p = sortPerfectMatch(a, b, filterValue);
        if (p !== 0) return p;

        return Sort.localeCompare(a.name, b.name, sortAscending);
    });
}

function filterZones(
    zones: Readonly<ZonesOfCityState.Zone[]>,
    filterValue: string,
): ZonesOfCityState.Zone[] {
    return zones.filter((zone: ZonesOfCityState.Zone) => {
        return (
            (isDefined(zone.extZoneCode) &&
                String.normalizedIncludes(
                    getOrElse<number>(zone.extZoneCode, 0).toString(),
                    filterValue,
                )) ||
            String.normalizedIncludes(zone.name, filterValue)
        );
    });
}

interface ZoneDropinState {
    settings: SettingsState.State;
    zoneDropin: ZoneDropinState.State;
    selection: ParkOptionListState.Selection.State;
}

interface ZoneDropinRenderValue {
    items: JSX.Element[];
    selectedItemRef: HTMLDivElement | null;
    cropped: boolean;
}

const OptionIcon = (p: {
    small: boolean;
    icon: JSX.Element;
    selected: boolean;
}) => {
    return (
        <div
            className={css([
                {
                    position: 'relative',
                    color: Colors.blue,
                    top: '13px',
                    marginTop: '-26px',
                },
                p.small && {
                    marginLeft: '11px',
                },
                p.selected && {
                    color: Colors.white,
                },
            ])}
        >
            <IconBig icon={p.icon} />
        </div>
    );
};

interface ZoneSelectionSlideInProps {
    zones: Readonly<ZonesOfCityState.Zone[]>;
    selectedCity: CitiesState.City;
}

export const ZoneSelectionSlideIn = Flux.selectState<
    ZoneSelectionSlideInProps,
    ZoneDropinState
>(
    s => ({
        settings: new SettingsState.StateSlice(s).state,
        zoneDropin: new ZoneDropinState.StateSlice(s).state,
        selection: ParkOptionListState.Selection.get(s),
    }),
    p => {
        return <ZoneSelectionSlideInContent {...p} />;
    },
);

const ZONE_LIST_CUTOFF_THRESHOLD = 150;

function ZoneSelectionSlideInContent(
    props: ZoneDropinState & {
        update: Flux.Updater;
    } & ZoneSelectionSlideInProps,
) {
    const selectedItemRef = useRef<HTMLDivElement>(null);

    function renderItems(): ZoneDropinRenderValue {
        const selectableParkOptions = props.zones;
        return {
            items: sortZones(
                filterZones(
                    selectableParkOptions,
                    props.zoneDropin.filterValue,
                ),
                props.zoneDropin.sortField,
                props.zoneDropin.sortAscending,
                props.zoneDropin.filterValue,
            )
                .slice(0, ZONE_LIST_CUTOFF_THRESHOLD)
                .map((zone: ZonesOfCityState.Zone) => {
                    const selected = thenElse(
                        props.selection.selectedParkOptionId,
                        v => v === zone.id,
                        false,
                    );

                    return (
                        <SlideInContentItem
                            onClick={() => {
                                props.update(s =>
                                    logAction(s, 'select-zone-from-dropin', {
                                        zipCode:
                                            selectableParkOptions[0].zipCode,
                                        zoneId: zone.id,
                                        itemCount: selectableParkOptions.length,
                                        searchUsed:
                                            !!props.zoneDropin.filterValue,
                                        openDuration: props.zoneDropin.openEpoch
                                            ? new Date().getTime() -
                                              props.zoneDropin.openEpoch
                                            : null,
                                    }),
                                );
                                props.update(store =>
                                    ParkCreateActions.setParkOption(
                                        store,
                                        zone.id,
                                    ),
                                );
                            }}
                            selected={selected}
                            key={zone.id}
                            itemRef={selected ? selectedItemRef : undefined}
                        >
                            <SlideInContentFixedColumn width={48}>
                                <SlideInContentItemText>
                                    {Highlight.highlightPartialMatch({
                                        text: isDefined(zone.extZoneCode)
                                            ? getOrElse<number>(
                                                  zone.extZoneCode,
                                                  0,
                                              ).toString()
                                            : '',
                                        className: css({ ...Typo.bodyBold }),
                                        filterValue:
                                            props.zoneDropin.filterValue,
                                        filterActive:
                                            ZoneDropinState.filterActive(
                                                props.zoneDropin,
                                            ),
                                    })}
                                </SlideInContentItemText>
                            </SlideInContentFixedColumn>
                            <SlideInContentFlexColumn>
                                <SlideInContentItemText>
                                    {Highlight.highlightPartialMatch({
                                        text: zone.name.replace(
                                            /\//g,
                                            '/\u200b',
                                        ),
                                        className: css({ ...Typo.bodyBold }),
                                        filterValue:
                                            props.zoneDropin.filterValue,
                                        filterActive:
                                            ZoneDropinState.filterActive(
                                                props.zoneDropin,
                                            ),
                                    })}
                                </SlideInContentItemText>
                            </SlideInContentFlexColumn>
                            <SlideInContentFixedColumn width={48}>
                                <ZoneTypeIcon
                                    variants={zone.variants}
                                    selected={selected}
                                />
                            </SlideInContentFixedColumn>
                        </SlideInContentItem>
                    );
                }),
            selectedItemRef: selectedItemRef.current,
            cropped: selectableParkOptions.length > ZONE_LIST_CUTOFF_THRESHOLD,
        };
    }

    const texts = DropinTexts.zoneDropinTexts[props.settings.language];
    const {
        items,
        selectedItemRef: selectedItemElement,
        cropped,
    } = renderItems();

    return (
        <ParkCreatePortalSlideIn
            open={props.zoneDropin.open}
            title={texts.Title(
                props.selectedCity && props.selectedCity.names.length === 1
                    ? props.selectedCity.names[0]
                    : '',
            )}
            onClose={() => props.update(DropinActions.ZoneDropin.close)}
        >
            <SlideInSearchFieldContainer>
                <SearchField
                    labelText={texts.TitleSearch()}
                    elevatedLabelText={texts.TitleSearch()}
                    value={props.zoneDropin.filterValue}
                    onChange={(value: string) =>
                        props.update(store =>
                            DropinActions.ZoneDropin.setFilterValue(
                                store,
                                value,
                            ),
                        )
                    }
                    onClear={() =>
                        props.update(DropinActions.ZoneDropin.clearFilterValue)
                    }
                    disabled={!props.zoneDropin.open}
                    searchFieldStyle={SearchFieldStyle.BLUE_GRAY}
                />
            </SlideInSearchFieldContainer>
            {cropped && (
                <div style={{ padding: '13px 24px' }}>
                    <InlineInfoBox
                        titleCaption={
                            <Localized
                                de="Liste zu lang"
                                fr="Liste trop longue"
                                it="Lista troppo lunga"
                                en="List too long"
                            />
                        }
                        arrowPosition={ArrowPosition.none}
                    >
                        <p>
                            <Localized
                                de="Die Liste unten ist zu lang und daher kann sie nicht vollständig angezeigt werden. Bitte benutze die Suche, um die Einträge zu reduzieren."
                                fr="La liste ci-dessous est trop longue et ne peut donc pas être affichée en entier. Utilise la recherche pour réduire le nombre d'entrées."
                                it="La lista sottostante è troppo lunga e quindi non può essere visualizzata per intero. Voglia usare la ricerca per ridurre i risultati."
                                en="The list below is too long and therefore cannot be displayed in full. Please use the search to reduce the entries."
                            />
                        </p>
                    </InlineInfoBox>
                </div>
            )}
            <SlideInContentHeader>
                <SlideInContentFixedColumn width={48} spacingLeft={false}>
                    <SlideInContentHeaderSortableItem
                        label={texts.ZoneIdLabel()}
                        active={
                            props.zoneDropin.sortField ===
                            ZoneDropinState.SortField.ZoneId
                        }
                        sortAscending={props.zoneDropin.sortAscending}
                        onClick={() =>
                            props.update(DropinActions.ZoneDropin.sortByZoneId)
                        }
                    />
                </SlideInContentFixedColumn>
                <SlideInContentFlexColumn>
                    <SlideInContentHeaderSortableItem
                        label={texts.ZoneNameLabel()}
                        active={
                            props.zoneDropin.sortField ===
                            ZoneDropinState.SortField.ZoneName
                        }
                        sortAscending={props.zoneDropin.sortAscending}
                        onClick={() =>
                            props.update(
                                DropinActions.ZoneDropin.sortByZoneName,
                            )
                        }
                    />
                </SlideInContentFlexColumn>
                <SlideInContentFixedColumn width={48} spacingRight={false} />
            </SlideInContentHeader>
            <SlideInContentBody
                updateScrollPosition={props.zoneDropin.open}
                selectedItemRef={selectedItemElement}
            >
                {items}
            </SlideInContentBody>
        </ParkCreatePortalSlideIn>
    );
}

function ZoneTypeIcon(props: {
    variants: ZonesOfCityState.ParkOptionVariant[];
    selected: boolean;
}): JSX.Element {
    const ot = {
        zoneEnforcedCheckin: props.variants.includes(
            ZonesOfCityState.ParkOptionVariant.zoneEnforcedCheckin,
        ),
        permits: props.variants.includes(
            ZonesOfCityState.ParkOptionVariant.permit,
        ),
        barrierGateCheckin: props.variants.includes(
            ZonesOfCityState.ParkOptionVariant.barrierGateCheckin,
        ),
    };

    if (ot.zoneEnforcedCheckin && ot.permits) {
        return (
            <OptionIcon
                small={false}
                icon={Icons48.onstreetPermit}
                selected={props.selected}
            />
        );
    } else if (ot.zoneEnforcedCheckin) {
        return (
            <OptionIcon
                small={true}
                icon={Icons48.onstreetSmall}
                selected={props.selected}
            />
        );
    } else if (ot.barrierGateCheckin && ot.permits) {
        return (
            <OptionIcon
                small={false}
                icon={Icons48.offstreetPermit}
                selected={props.selected}
            />
        );
    } else if (ot.barrierGateCheckin) {
        return (
            <OptionIcon
                small={true}
                icon={Icons48.offstreet}
                selected={props.selected}
            />
        );
    } else {
        return (
            <OptionIcon
                small={true}
                icon={Icons48.permitSmall}
                selected={props.selected}
            />
        );
    }
}
